import {
  ActionButton,
  OfficeButton,
  OfficeHeader,
  Paginator,
  RegularInsurerPicker,
  TableComponent,
  TableLoader,
} from "components";
import {
  DocumentPlusIcon,
  RectangleStackIcon,
} from "@heroicons/react/24/outline";
import { useNavigate, useSearch } from "react-location";
import { useUrlState, withPermissions } from "utils";

import { Action } from "components/buttons/action-button";
import CreateContainer from "./create";
import { FC } from "react";
import { LocationGenerics } from "router/location";
import RemoveContainer from "./remove";
import { TreatyPrograms_rows } from "_graphql/queries/__generated__/TreatyPrograms";
import UpdateContainer from "./update";
import ViewContainer from "./view";
import _ from "lodash";
import clsx from "clsx";
import moment from "moment";
import { usePagination } from "hooks";
import { useTreatyPrograms } from "hooks/data/use-treaty-programs";

type Props = {};

const TreatyProgramsPage: FC<Props> = (props) => {
  const [modal, setModal] = useUrlState("modal");
  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const { offset, limit, search, setPage } = usePagination();
  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  const expandItem = (id: string) => () => {
    navigate({
      to: `./${id}/view`,
    });
  };

  const { treatyPrograms, loading, refetch, count } = useTreatyPrograms({
    args: {
      insurer_id: (searchParams.insurer as string) || "",
      limit,
      offset,
      search,
      treaty_type: [],
    },
  });

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <OfficeHeader
        renderActions={() => (
          <>
            {" "}
            {withPermissions([
              "*:*",
              "treaties:*",
              "treaties:manage",
              "treaties:create",
            ])(
              <OfficeButton onClick={() => setModal("create")}>
                <DocumentPlusIcon className="w-5 h-5 mr-2" />
                <span className="hidden sm:inline">Create Treaty Program</span>
              </OfficeButton>
            )}
          </>
        )}
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            title={"Class of Business"}
            hasSearch
            renderActions={() => (
              <>
                <RegularInsurerPicker
                  id={"insurer"}
                  label={""}
                  rawId
                  values={searchParams}
                  setFieldValue={(key: string, value: string) =>
                    navigate({
                      search(prev) {
                        return {
                          ...prev,
                          [key]: value,
                        };
                      },
                    })
                  }
                  setFieldTouched={() => {}}
                  setFieldError={() => {}}
                />
              </>
            )}
            columns={[
              {
                name: "Treaty Program",
                accessor: "treaty_name",
                render: (row: TreatyPrograms_rows) => (
                  <div className="flex items-center">
                    <RectangleStackIcon className="w-7 h-7 mr-2.5 flex-shrink-0 text-gray-400" />
                    <div className="flex flex-col">
                      <button
                        onClick={expandItem(row.treaty_program_id || "")}
                        className="font-semibold text-sm text-left hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                        title={`View details for ${row.treaty_name}`}
                      >
                        {row.treaty_name || "Unnamed Program"}
                      </button>
                      <span
                        className={clsx("font-light text-xs capitalize", {
                          "text-green-600": row.treaty_type === "PROPORTIONAL",
                          "text-blue-700":
                            row.treaty_type === "NONPROPORTIONAL",
                          "text-gray-500": !row.treaty_type,
                        })}
                      >
                        {row.treaty_type
                          ? _.startCase(row.treaty_type.toLowerCase())
                          : "Unknown Type"}
                      </span>
                    </div>
                  </div>
                ),
              },
              {
                name: "Ceeding Company",
                accessor: "insurer.insurer_company_name",
                render: (row: TreatyPrograms_rows) => (
                  <span
                    className="block truncate max-w-xs"
                    title={row.insurer?.insurer_company_name ?? "N/A"}
                  >
                    {row.insurer?.insurer_company_name || "N/A"}
                  </span>
                ),
              },
              {
                name: "Period",
                accessor: "treaty_associate_deductions[0].treaty_period_from",
                render: (row: TreatyPrograms_rows) => {
                  const firstDeduction = _.first(
                    row?.treaty_associate_deductions
                  );
                  const fromDate = firstDeduction?.treaty_period_from;
                  const toDate = firstDeduction?.treaty_period_to;

                  const formattedFrom = fromDate
                    ? moment(fromDate).format("DD MMM YYYY")
                    : "N/A";
                  const formattedTo = toDate
                    ? moment(toDate).format("DD MMM YYYY")
                    : "N/A";

                  return (
                    <div className="flex flex-col text-xs">
                      <span>
                        From:{" "}
                        <span className="font-medium text-gray-500">
                          {formattedFrom}
                        </span>
                      </span>
                      <span>
                        To:{" "}
                        <span className="font-medium text-gray-500">
                          {formattedTo}
                        </span>
                      </span>
                    </div>
                  );
                },
              },
              {
                name: "Actions",
                accessor: "actions",
                render: (row: TreatyPrograms_rows) => (
                  <div className="flex items-center space-x-1">
                    <ActionButton
                      action="view"
                      tooltip="View Program Details"
                      onClick={dispatchAction(
                        row.treaty_program_id || "",
                        "view"
                      )}
                    />
                    {withPermissions([
                      "*:*",
                      "treaties:*",
                      "treaties:manage",
                      "treaties:update",
                    ])(
                      <ActionButton
                        action="update"
                        tooltip="Edit Program"
                        onClick={dispatchAction(
                          row.treaty_program_id || "",
                          "update"
                        )}
                      />
                    )}

                    <ActionButton
                      action="goto"
                      tooltip="View Deductions & Details"
                      onClick={expandItem(row.treaty_program_id || "")}
                    />
                    {withPermissions([
                      "*:*",
                      "treaties:manage",
                      "treaties:delete",
                    ])(
                      <ActionButton
                        action="remove"
                        tooltip="Delete Program"
                        onClick={dispatchAction(
                          row.treaty_program_id || "",
                          "remove"
                        )}
                      />
                    )}
                  </div>
                ),
              },
            ]}
            renderLoader={() => <TableLoader items={[1, 1, 1, 1]} />}
            loading={loading}
            data={treatyPrograms}
            refetch={refetch}
            pagination={() => (
              <Paginator
                offset={offset}
                limit={limit}
                totalItems={count}
                currentSize={treatyPrograms.length}
                setOffset={setPage}
              />
            )}
          />
        </div>
      </div>

      <CreateContainer
        open={modal === "create"}
        setOpen={(val: boolean) => setModal(val ? "create" : undefined)}
        refetch={refetch}
      />
      {searchParams.id && (
        <>
          <ViewContainer
            open={modal === "view"}
            setOpen={(val: boolean) => setModal(val ? "view" : undefined)}
          />
          <UpdateContainer
            open={modal === "update"}
            setOpen={(val: boolean) => setModal(val ? "update" : undefined)}
            refetch={refetch}
          />
          <RemoveContainer
            open={modal === "remove"}
            setOpen={(val: boolean) => setModal(val ? "remove" : undefined)}
            refetch={refetch}
          />
        </>
      )}
    </main>
  );
};

export default TreatyProgramsPage;
